import * as React from 'react'
import { ExtensionSlot, LayoutProps, Menu } from 'piral'
import Logo from './Logo'
import { Route, Redirect } from 'react-router-dom'
import Footer from './Footer'
import KebabMenu from './KebabMenu'
import LanguageSwitcher from './LanguageSwitcher'
import CookieBanner from './CookieBanner'
import BetaBanner from './BetaBanner'
import LoadingScreen from './LoadingScreen'
import StartupError from './StartupError'
import PageTransition from './PageTransition'
import { ConsentProvider } from '../consent/ConsentProvider'
import { AuthProvider, useAuth } from '../auth/AuthProvider'
import { useViewTracking } from '../telemetry/useViewTracking'
import { useHeaderScrim } from './useHeaderScrim'

/**
 * Keeps anonymous visitors out of routes a pilet marked as behind a session (its
 * menu entry carries `secured`). Renders nothing of its own — it only redirects,
 * and only once `whoami` has answered, so an authenticated visitor who hard-loads
 * a protected URL is never bounced during the in-flight window.
 *
 * Lives beside its matching route rather than replacing it: Piral owns the page
 * registration, so the shell guards the path from the outside.
 */
const RouteGuard: React.FC<{ path: string }> = ({ path }) => {
  const { user, ready } = useAuth()
  return (
    <Route exact path={path}>
      {ready && !user ? <Redirect to='/' /> : null}
    </Route>
  )
}

/**
 * The shell chrome and pilet content, held behind the {@link LoadingScreen} until
 * the startup handshake settles. `useAuth().ready` flips true once `whoami` and the
 * domain configuration have answered and — for a signed-in visitor — device
 * registration has finished, so nothing here (header, page, or route guard) mounts
 * before those APIs have been called. This is the shell-level enforcement of
 * "whoami and the domain configuration first"; the guards below then only ever see
 * a fully-known session.
 *
 * If the handshake settled *badly*, the app never mounts at all — {@link StartupError}
 * takes the whole viewport instead. Read `failed` before `ready`: a failure also
 * flips `ready`, since the handshake did finish.
 */
const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { ready, failed, blocked } = useAuth()
  // Drives the header's scroll scrim. Called before the early returns so the hook
  // order stays fixed across the loading, error and app renders; the callback simply
  // does not fire until the header below actually mounts.
  const headerScrimRef = useHeaderScrim()
  if (failed) return <StartupError reason={failed} />
  // Same page, not a failure: a members-only library and an anonymous visitor we
  // could not redirect to sign in. Read after `failed` — a real fault outranks it.
  if (blocked) return <StartupError reason={blocked} />
  if (!ready) return <LoadingScreen />
  return (
    <>
      {/* First focus stop on the page: lets a keyboard or remote viewer jump the
          header nav straight to the content (WCAG 2.4.1). Off-screen until focused. */}
      <a className='SkipLink' href='#main-content'>Skip to content</a>
      {/* Shell chrome: a beta notice pinned above the header on every page. */}
      <BetaBanner />
      <div className='MainWrapper'>
        {/* No LayoutContainer here on purpose: the main navigation is full-bleed
            chrome, so it spans the viewport and only its own padding insets the
            content — unlike the page body, which stays inside the max-width. */}
        <header className='Header' ref={headerScrimRef} data-scrolled='false'>
          <Logo />
          <Menu type='general' />
          {/* Header actions render in ascending `order` (their registerExtension
              `defaults.order`) rather than in non-deterministic pilet load order,
              so each pilet owns its own position — the same idea as the menu's
              `order` sort and the MovieTile's ListToggle stack. The kebab is shell
              chrome, not a pilet, so it stays last by construction. */}
          {/* Language is shell-owned state, so its control is shell chrome. It
              renders BEFORE the pilet header actions so the search action — last
              in the slot (its `order` is highest) — stays directly against the
              kebab, as designed. */}
          <LanguageSwitcher />
          <ExtensionSlot
            name='header-items'
            order={exts => [...exts].sort((a, b) => (a.defaults?.order ?? 0) - (b.defaults?.order ?? 0))}
          />
          <KebabMenu />
        </header>
        <main id='main-content' className='MainContent'>
          <Route exact path='/'>
            <Redirect to='/videos' />
          </Route>
          {/* Favorites live behind a session (its menu entry is `secured`). */}
          <RouteGuard path='/favorites' />
          {/* Route changes fade in and reset the scroll offset, once for every
              pilet — see PageTransition. Only the page content is wrapped: the
              header and footer are persistent chrome and must not re-animate. */}
          <PageTransition>{children}</PageTransition>
          {/* Portal footer slot: pilet-contributed rows below every page's content
              (e.g. the favorites pilet's watch-later list). Ordered by each
              registration's `order` (lower first) — the footer mirror of the
              `header-items` slot above. A pilet owns its own position; the slot
              renders nothing when no pilet contributes. */}
        </main>
      </div>
      <Footer />
    </>
  )
}

/** The consent banner, held behind the same startup gate as the app chrome. */
const GatedCookieBanner: React.FC = () => {
  const { ready, failed, blocked } = useAuth()
  if (!ready || failed || blocked) return null
  return <CookieBanner />
}

// No <BrowserRouter> here: Piral already establishes the router context around
// the layout. Nesting a second router only stays in sync via window.history and
// can desync route matching (the classic Piral "double router" foot-gun), so we
// consume the shell's router directly.
const App: React.FC<LayoutProps> = ({ children }) => {
  // Names the current route pattern as the telemetry "view", so web vitals and errors
  // aggregate per page rather than per URL. Here rather than inside `Shell` so a view
  // is named even while the startup handshake holds the loading screen up — a visitor
  // who never gets past it is exactly the case worth seeing in the dashboard. Inert
  // until analytics consent is granted.
  useViewTracking()

  return (
    <ConsentProvider>
      {/* Wraps the header and main both: the nav reads auth to hide `secured` items,
          the guards read it to protect their routes, and the Shell gates first paint
          on the same handshake so whoami/device run before any of it. */}
      <AuthProvider>
        <Shell>{children}</Shell>
        {/* Inside the AuthProvider, and gated on the same handshake: the consent
            banner is app chrome, so it must not float over the startup loader or
            the error page. Delaying it costs nothing — the analytics loaders it
            unlocks react to the decision whenever it lands. */}
        <GatedCookieBanner />
      </AuthProvider>
    </ConsentProvider>
  )
}

export default App
